import React, { useCallback, useEffect, useRef, useState } from 'react';
import { recordScore, getProgress, setLevel, unlockNext, getHighScore } from '../gamesLib';
import { sfx } from '../gameSound';

interface Props {
  onExit: () => void;
  challengeScore?: number | null;
}

const W = 440;
const H = 300;
const PADDLE_H = 60;
const PADDLE_W = 10;
const BALL_R = 7;
const WIN_POINTS = 7;
// AI tracking speed + ball speed per level.
const LEVELS = [
  { name: 'Easy', ai: 3.0, ball: 4.0 },
  { name: 'Medium', ai: 4.2, ball: 4.8 },
  { name: 'Hard', ai: 5.6, ball: 5.6 },
];

const GamePong: React.FC<Props> = ({ onExit, challengeScore }) => {
  const prog = getProgress('pong');
  const [level, setLvl] = useState(prog.level || 1);
  const [playerScore, setPlayerScore] = useState(0);
  const [aiScore, setAiScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [best] = useState(getHighScore('pong'));
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<'win' | 'lose' | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const savedRef = useRef(0);

  const g = useRef({
    playerY: H / 2 - PADDLE_H / 2,
    aiY: H / 2 - PADDLE_H / 2,
    ball: { x: W / 2, y: H / 2, vx: 4, vy: 2 },
    pScore: 0,
    aScore: 0,
    running: false,
    aiSpeed: 3,
    ballSpeed: 4,
  });

  const serve = useCallback((toPlayer: boolean) => {
    const speed = g.current.ballSpeed;
    const dir = toPlayer ? -1 : 1;
    const angle = (Math.random() * 0.6 - 0.3);
    g.current.ball = { x: W / 2, y: H / 2, vx: dir * speed * Math.cos(angle), vy: speed * Math.sin(angle) };
  }, []);

  const startMatch = useCallback((lvl: number) => {
    const c = LEVELS[Math.min(lvl - 1, LEVELS.length - 1)];
    g.current.aiSpeed = c.ai;
    g.current.ballSpeed = c.ball;
    g.current.pScore = 0;
    g.current.aScore = 0;
    g.current.running = false;
    g.current.playerY = H / 2 - PADDLE_H / 2;
    g.current.aiY = H / 2 - PADDLE_H / 2;
    serve(Math.random() < 0.5);
    setPlayerScore(0);
    setAiScore(0);
    setResult(null);
    setRunning(false);
    savedRef.current = streak; // keep current streak baseline
    setLvl(lvl);
    setLevel('pong', lvl);
  }, [serve, streak]);

  useEffect(() => {
    startMatch(prog.level || 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const endMatch = useCallback((won: boolean) => {
    g.current.running = false;
    setRunning(false);
    setResult(won ? 'win' : 'lose');
    if (won) {
      sfx.win();
      setStreak(s => s + 1);
      if (level < LEVELS.length) unlockNext('pong', level);
    } else {
      sfx.lose();
      setStreak(0);
    }
  }, [level]);

  // Record best streak.
  useEffect(() => {
    if (streak > savedRef.current) {
      savedRef.current = streak;
      recordScore('pong', streak, level);
    }
  }, [streak, level]);

  useEffect(() => {
    let raf = 0;
    const ctx = canvasRef.current?.getContext('2d');
    const loop = () => {
      const s = g.current;
      const c = ctx;
      if (c) {
        c.fillStyle = '#0f1226';
        c.fillRect(0, 0, W, H);
        // Net.
        c.strokeStyle = 'rgba(255,255,255,0.2)';
        c.setLineDash([6, 8]);
        c.beginPath(); c.moveTo(W / 2, 0); c.lineTo(W / 2, H); c.stroke();
        c.setLineDash([]);

        if (s.running) {
          const b = s.ball;
          b.x += b.vx; b.y += b.vy;
          if (b.y - BALL_R < 0) { b.y = BALL_R; b.vy *= -1; sfx.move(); }
          if (b.y + BALL_R > H) { b.y = H - BALL_R; b.vy *= -1; sfx.move(); }

          // Player paddle (left).
          if (b.x - BALL_R < PADDLE_W + 6 && b.y > s.playerY && b.y < s.playerY + PADDLE_H && b.vx < 0) {
            b.vx = Math.abs(b.vx);
            const off = (b.y - (s.playerY + PADDLE_H / 2)) / (PADDLE_H / 2);
            b.vy = s.ballSpeed * off;
            sfx.pop();
          }
          // AI paddle (right).
          if (b.x + BALL_R > W - PADDLE_W - 6 && b.y > s.aiY && b.y < s.aiY + PADDLE_H && b.vx > 0) {
            b.vx = -Math.abs(b.vx);
            const off = (b.y - (s.aiY + PADDLE_H / 2)) / (PADDLE_H / 2);
            b.vy = s.ballSpeed * off;
            sfx.pop();
          }
          // AI tracking.
          const target = b.y - PADDLE_H / 2;
          if (s.aiY + 2 < target) s.aiY = Math.min(s.aiY + s.aiSpeed, H - PADDLE_H);
          else if (s.aiY - 2 > target) s.aiY = Math.max(s.aiY - s.aiSpeed, 0);

          // Scoring.
          if (b.x < -BALL_R) {
            s.aScore += 1; setAiScore(s.aScore); sfx.score();
            if (s.aScore >= WIN_POINTS) endMatch(false);
            else { s.running = false; setRunning(false); serve(false); }
          } else if (b.x > W + BALL_R) {
            s.pScore += 1; setPlayerScore(s.pScore); sfx.score();
            if (s.pScore >= WIN_POINTS) endMatch(true);
            else { s.running = false; setRunning(false); serve(true); }
          }
        }

        // Paddles.
        c.fillStyle = '#66bb6a';
        c.fillRect(6, s.playerY, PADDLE_W, PADDLE_H);
        c.fillStyle = '#ef5350';
        c.fillRect(W - PADDLE_W - 6, s.aiY, PADDLE_W, PADDLE_H);
        // Ball.
        c.fillStyle = '#ffd54f';
        c.beginPath(); c.arc(s.ball.x, s.ball.y, BALL_R, 0, Math.PI * 2); c.fill();
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [serve, endMatch]);

  const movePaddle = (clientY: number) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const y = ((clientY - rect.top) / rect.height) * H - PADDLE_H / 2;
    g.current.playerY = Math.max(0, Math.min(H - PADDLE_H, y));
  };

  const launch = () => {
    if (result) return;
    g.current.running = true;
    setRunning(true);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const step = 28;
      if (e.key === 'ArrowUp' || e.key === 'w') { g.current.playerY = Math.max(0, g.current.playerY - step); }
      else if (e.key === 'ArrowDown' || e.key === 's') { g.current.playerY = Math.min(H - PADDLE_H, g.current.playerY + step); }
      else if (e.key === ' ') { e.preventDefault(); launch(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line
  }, [result]);

  return (
    <div className="gpong">
      <div className="game-toolbar">
        <button className="game-back" onClick={onExit}>← Arcade</button>
        <div className="game-stats">
          <span className="gstat"><b>{playerScore}–{aiScore}</b><i>you–cpu</i></span>
          <span className="gstat"><b>{streak}</b><i>streak</i></span>
          <span className="gstat"><b>{Math.max(best, streak)}</b><i>best</i></span>
        </div>
        <button className="game-restart" onClick={() => startMatch(level)}>↻ New</button>
      </div>

      <div className="g2048-levels">
        {LEVELS.map((lv, i) => {
          const lvl = i + 1;
          const unlocked = lvl <= getProgress('pong').unlockedLevel;
          return (
            <button key={lv.name} className={`lvl-pill ${level === lvl ? 'active' : ''} ${unlocked ? '' : 'locked'}`}
              onClick={() => unlocked && startMatch(lvl)} disabled={!unlocked}>
              {unlocked ? lv.name : '🔒'}
            </button>
          );
        })}
      </div>

      {challengeScore != null && (
        <div className="challenge-target">🎯 Beat the challenge: <b>{challengeScore}</b> win streak</div>
      )}

      <div className="gpong-wrap" style={{ width: W, maxWidth: '100%' }}>
        <canvas
          ref={canvasRef}
          width={W}
          height={H}
          className="gpong-canvas"
          onMouseMove={e => movePaddle(e.clientY)}
          onTouchMove={e => { if (e.touches[0]) { e.preventDefault(); movePaddle(e.touches[0].clientY); } }}
          onClick={launch}
        />
        {!running && !result && (
          <div className="gpong-hint" onClick={launch}>First to {WIN_POINTS}. Move with mouse / finger / ↑↓ · click or Space to serve 🏓</div>
        )}
      </div>

      {result && (
        <div className="game-overlay">
          <div className="game-overlay-card">
            <div className="go-emoji">{result === 'win' ? '🏆' : '🤖'}</div>
            <h3>{result === 'win' ? 'You win the match!' : 'CPU wins'}</h3>
            <p>{playerScore}–{aiScore}{result === 'win' ? ` · streak ${streak}${level < LEVELS.length ? ' · next level unlocked' : ''}` : ' · streak reset'}</p>
            <div className="go-actions">
              {result === 'win' && level < LEVELS.length && (
                <button className="g-btn primary" onClick={() => startMatch(level + 1)}>Next level →</button>
              )}
              <button className="g-btn" onClick={() => startMatch(level)}>Play again</button>
              <button className="g-btn ghost" onClick={onExit}>Back to arcade</button>
            </div>
          </div>
        </div>
      )}

      <p className="game-hint">Move with mouse / finger or ↑ ↓. First to {WIN_POINTS} points wins the match.</p>
    </div>
  );
};

export default GamePong;
